// src/hooks/authStateRoles.ts
// Pure derivation of role/membership/platform-role state from raw user_roles
// rows + the user's profile. Extracted from useAuthState so:
//   1) the file-size hygiene cap on useAuthState stays satisfied, and
//   2) the role math is unit-testable without mocking React or Supabase.
//
// Key behavior (slice "auth-platform-role-split"):
//  - Platform roles (platform_owner / _admin / _dev / _trainer) are treated as
//    identity-level. They are accepted from any user_roles row regardless of
//    the row's `org_id` value, so a misfiled row (org_id != null) does NOT
//    silently drop platform access. Misfiled rows are surfaced via the
//    returned `warnings[]` list so the caller can console.warn them once.
//  - Org-scoped roles (sales / trainer / owner / etc.) only count when the
//    row has an org_id and the user's accessStatus is "approved".
//  - activeMembership / role / platformRole stay gated on accessStatus =
//    "approved" to preserve pre-existing behavior for pending/blocked users.
import type {
  AccessStatus,
  ActiveMembership,
  AuthProfile,
  OrgRole,
  PlatformRole,
} from "./authStateModel";

export type UserRolesRow = {
  role: string | null;
  org_id: string | null;
};

export type OrgRoleEntry = {
  orgId: string;
  role: string;
};

export type DeriveAuthRolesInput = {
  rows: UserRolesRow[];
  profile: AuthProfile | null;
  accessStatus: AccessStatus;
};

export type DeriveAuthRolesResult = {
  activeMembership: ActiveMembership | null;
  role: OrgRole;
  orgRoles: OrgRoleEntry[];
  platformRoles: string[];
  platformRole: PlatformRole;
  allRoles: string[];
  memberOrganizationIds: string[];
  /** Human-readable strings describing rows that violated the platform-role schema. */
  warnings: string[];
};

const PLATFORM_ROLE_VALUES = [
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

type PlatformRoleString = (typeof PLATFORM_ROLE_VALUES)[number];

/** Highest-precedence-first. Matches the precedence used in AuthContext.platformRoleFromTokens. */
const PLATFORM_ROLE_PRECEDENCE: PlatformRoleString[] = [
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
];

function isPlatformRoleString(value: string): value is PlatformRoleString {
  return (PLATFORM_ROLE_VALUES as readonly string[]).includes(value);
}

function normalizeRole(raw: string | null): string {
  return String(raw ?? "").trim().toLowerCase();
}

function activeMembershipRoleOk(role: string): role is ActiveMembership["role"] {
  if (!role || role === "unassigned") return false;
  return true;
}

export function deriveAuthRolesFromUserRoles(input: DeriveAuthRolesInput): DeriveAuthRolesResult {
  const { rows, profile, accessStatus } = input;
  const empty: DeriveAuthRolesResult = {
    activeMembership: null,
    role: "unassigned",
    orgRoles: [],
    platformRoles: [],
    platformRole: null,
    allRoles: [],
    memberOrganizationIds: [],
    warnings: [],
  };

  if (!profile || !Array.isArray(rows)) return empty;

  // `allRoles` is the flat string list used by grantedRoleTokens for the
  // backward-compatible hasRole() union. We keep populating it from every row
  // (regardless of org_id) so misfiled platform rows still surface there too.
  const allRoles: string[] = rows.map((r) => String(r.role ?? "").trim()).filter(Boolean);

  const memberOrganizationIds = Array.from(
    new Set(
      rows
        .map((r) => r.org_id)
        .filter((id): id is string => typeof id === "string" && id.length > 0)
    )
  );

  // Scan for platform_* roles in ANY row; warn on misfiles (org_id != null).
  const warnings: string[] = [];
  const platformRolesSet = new Set<string>();
  for (const row of rows) {
    const r = normalizeRole(row.role);
    if (!isPlatformRoleString(r)) continue;
    platformRolesSet.add(r);
    if (row.org_id !== null) {
      warnings.push(
        `platform role "${r}" filed against org_id="${row.org_id}" (expected org_id=null); honoring as identity-level`
      );
    }
  }
  const platformRoles = Array.from(platformRolesSet);

  if (accessStatus !== "approved") {
    return {
      activeMembership: null,
      role: "unassigned",
      orgRoles: [],
      platformRoles,
      platformRole: null,
      allRoles,
      memberOrganizationIds,
      warnings,
    };
  }

  // Org-scoped rows: must have an org_id, must have a non-empty role string,
  // and must not be a platform_* value (those are identity-level by convention
  // and stay out of `orgRoles` / `activeMembership` even when misfiled).
  const orgRoleRows = rows.filter(
    (r): r is UserRolesRow & { org_id: string; role: string } => {
      if (typeof r.org_id !== "string" || r.org_id.length === 0) return false;
      if (!r.role) return false;
      if (isPlatformRoleString(normalizeRole(r.role))) return false;
      return true;
    }
  );

  const orgRoles: OrgRoleEntry[] = orgRoleRows.map((r) => ({
    orgId: r.org_id,
    role: String(r.role).trim(),
  }));

  // Active membership: prefer the row whose org matches profile.primary_organization_id,
  // otherwise fall back to the first org-scoped row.
  let activeMembership: ActiveMembership | null = null;
  let role: OrgRole = "unassigned";
  const primaryMatch = profile.primary_organization_id
    ? orgRoleRows.find((r) => r.org_id === profile.primary_organization_id)
    : null;
  const chosenOrgRole = primaryMatch ?? orgRoleRows[0] ?? null;
  if (chosenOrgRole) {
    const orgRole = String(chosenOrgRole.role).trim();
    if (activeMembershipRoleOk(orgRole)) {
      activeMembership = {
        organization_id: chosenOrgRole.org_id,
        role: orgRole as ActiveMembership["role"],
      };
      role = orgRole as OrgRole;
    } else {
      role = "sales";
    }
  } else {
    role = "sales";
  }

  // Highest-precedence platform role for the typed enum surface.
  let platformRole: PlatformRole = null;
  for (const candidate of PLATFORM_ROLE_PRECEDENCE) {
    if (platformRolesSet.has(candidate)) {
      platformRole = candidate;
      break;
    }
  }

  return {
    activeMembership,
    role,
    orgRoles,
    platformRoles,
    platformRole,
    allRoles,
    memberOrganizationIds,
    warnings,
  };
}
